import React, { useCallback, useState, useRef } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import DatapointsEditTable from '../../DataSourceDetails/DatapointsEditTable'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'

const useStyles = makeStyles((theme) => ({
  container: {
  },
  header: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    maxHeight: '60vh',
    overflow: 'auto'
  },
  summary: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText
  }
}))

const useSubmitter = (dataSource, datapointsRef, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDatasourceDatapoints } = dataCatalog.useModifyDatasourceDatapoints()

  const onSubmit = useCallback(async () => {
    if (!datapointsRef.current) {
      setError('Unable to access datapoints table')
      return
    }

    const changes = datapointsRef.current.getChanges()

    // Check if there are any changes to save
    if (changes.added.length === 0 && changes.modified.length === 0) {
      setError('No changes detected to save')
      return
    }

    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      datapoints: datapointsRef.current.getData() // Send all current datapoints
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyDatasourceDatapoints(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the datapoints')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, datapointsRef, onComplete, modifyDatasourceDatapoints])

  return {
    onSubmit,
    processing,
    error
  }
}

function EditDatapointForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const datapointsRef = useRef()
  const [changesSummary, setChangesSummary] = useState({ added: 0, modified: 0 })

  const { onSubmit, processing, error } = useSubmitter(dataSource, datapointsRef, onComplete)

  const handleGetChanges = useCallback(() => {
    if (datapointsRef.current) {
      const changes = datapointsRef.current.getChanges()
      setChangesSummary({
        added: changes.added.length,
        modified: changes.modified.length
      })
    }
  }, [])

  const handleReset = useCallback(() => {
    if (datapointsRef.current) {
      datapointsRef.current.reset()
      setChangesSummary({ added: 0, modified: 0 })
    }
  }, [])

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        {/* Changes Summary */}
        {(changesSummary.added > 0 || changesSummary.modified > 0) && (
          <Grid item xs={12}>
            <div className={classes.summary}>
              <Typography variant='body2'>
                <strong>Pending Changes:</strong> {changesSummary.added} added, {changesSummary.modified} modified
              </Typography>
            </div>
          </Grid>
        )}

        {/* Datapoints Edit Table */}
        <Grid item xs={12}>
          <div className={classes.tableContainer}>
            <DatapointsEditTable
              ref={datapointsRef}
              initialDatapoints={dataSource.datapoints || []}
            />
          </div>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: 'auto' }}>
              <SydButton
                variant='ghost'
                size='sm'
                onClick={handleGetChanges}
                disabled={processing}
              >
                Check Changes
              </SydButton>
              <SydButton
                variant='ghost'
                size='sm'
                onClick={handleReset}
                disabled={processing}
              >
                Reset
              </SydButton>
            </div>

            {error && <div className='__error'>{error}</div>}

            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={onSubmit}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

EditDatapointForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subType: PropTypes.string.isRequired,
    description: PropTypes.string,
    datapoints: PropTypes.array
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default EditDatapointForm
